import React, { useEffect, useState } from 'react'
import { WifiOff, Wifi } from 'lucide-react'
import { getOfflineStatus } from '../../lib/pwa'

interface OfflineIndicatorProps {
  position?: 'top' | 'bottom'
  showOnlineMessage?: boolean
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'top',
  showOnlineMessage = true
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [showOnlineToast, setShowOnlineToast] = useState(false)
  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)

      // Show "back online" message if we were previously offline
      if (wasOffline && showOnlineMessage) {
        setShowOnlineToast(true)
        setTimeout(() => setShowOnlineToast(false), 3000)
      }

      setWasOffline(false)
    }

    const handleOffline = () => {
      setIsOnline(false)
      setWasOffline(true)
    }

    window.addEventListener('pwa-online', handleOnline)
    window.addEventListener('pwa-offline', handleOffline)

    // Also listen to native events as fallback
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('pwa-online', handleOnline)
      window.removeEventListener('pwa-offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [wasOffline, showOnlineMessage])

  // Show offline indicator
  if (!isOnline) {
    return (
      <div
        className={`fixed left-0 right-0 z-50 ${
          position === 'top' ? 'top-0' : 'bottom-0'
        }`}
        role="alert"
        aria-live="polite"
      >
        <div className="bg-yellow-500 text-black px-4 py-2 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-center space-x-2">
            <WifiOff size={16} />
            <span className="text-sm font-medium">
              You're offline. Some features may be limited.
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Show "back online" toast
  if (showOnlineToast) {
    return (
      <div
        className={`fixed left-4 right-4 z-50 animate-slide-down ${
          position === 'top' ? 'top-4' : 'bottom-4'
        }`}
        role="alert"
        aria-live="polite"
      >
        <div className="max-w-md mx-auto bg-green-500 text-white rounded-lg shadow-lg px-4 py-3">
          <div className="flex items-center justify-center space-x-2">
            <Wifi size={16} />
            <span className="text-sm font-medium">
              You're back online!
            </span>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default OfflineIndicator
